import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import requester, {
  UrlOptions,
} from '~/libs/api-hooks/architect/utils/requester';
import StatusError from '~/libs/api-hooks/architect/utils/statusError';
import mapApiObjectToCamelCase from '~/libs/api-hooks/architect/utils/mapApiObjectToCamelCase';
import { QueryMap } from '~/libs/api-hooks/architect/utils/prepareSearchParams';
import DeepPartial from '~/libs/api-hooks/architect/utils/DeepPartial';

export type ObjectApiOptions<
  ObjectType,
  DeepPartialObjectType extends
    DeepPartial<ObjectType> = DeepPartial<ObjectType>,
> = Omit<
  UseQueryOptions<DeepPartialObjectType, StatusError>,
  'queryKey' | 'queryFn'
>;

export type ObjectApiResult<
  ResponseDataType,
  DeepPartialObjectType extends
    DeepPartial<ResponseDataType> = DeepPartial<ResponseDataType>,
> = UseQueryResult<DeepPartialObjectType, StatusError>;

export interface UseObjectApiProps<
  ObjectType,
  DeepPartialObjectType extends
    DeepPartial<ObjectType> = DeepPartial<ObjectType>,
> {
  pathname: string;
  cacheKey: string[];
  queryMap?: QueryMap;
  reactQueryOptions?: ObjectApiOptions<ObjectType, DeepPartialObjectType>;
  urlOptions?: UrlOptions;
  includeApiKeyWhenAvailable?: boolean;
  customHeaders?: Record<string, string>;
  shouldMapResultToCamelCase?: boolean;
  timeoutMs?: number;
}

const useObjectApi = <
  ObjectType,
  DeepPartialObjectType extends
    DeepPartial<ObjectType> = DeepPartial<ObjectType>,
>({
  pathname,
  cacheKey,
  queryMap,
  reactQueryOptions = {},
  urlOptions,
  includeApiKeyWhenAvailable = true,
  customHeaders,
  shouldMapResultToCamelCase = true,
  timeoutMs,
}: UseObjectApiProps<ObjectType, DeepPartialObjectType>): ObjectApiResult<
  ObjectType,
  DeepPartialObjectType
> => {
  const fetchAPI = async (): Promise<DeepPartialObjectType> => {
    const response = await requester({
      pathname,
      queryMap,
      method: 'GET',
      urlOptions,
      includeApiKeyWhenAvailable,
      sendCsrfToken: false,
      customHeaders,
      timeoutMs,
    });
    try {
      const responseJson = await response.json();

      if (!shouldMapResultToCamelCase) {
        return responseJson;
      }

      return mapApiObjectToCamelCase<DeepPartialObjectType>(responseJson);
    } catch (error: any) {
      throw new StatusError({
        message: error.message,
        status: 0,
        pathname,
      });
    }
  };
  return useQuery<DeepPartialObjectType, StatusError>(cacheKey, fetchAPI, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    ...reactQueryOptions,
  });
};

export default useObjectApi;
